import { Reorder, useDragControls } from 'framer-motion';
import { QuizFragmentFragment } from '../../../apollo/__generated__/graphql';
import QuizQuestion from '../../../components/quizzes-browser/quiz-question';
import {
  createExpandedQuestionNameForQuiz,
} from '../../../components/quizzes-browser/quizzes-utils';
import DragAndPropIcon from '../../../assets/icons/drag-and-drop.svg?react';
import { ArrayElement } from '../../../utils/types';
import InlineFormattedText from '../../../components/common/inline-formatted-text.tsx';
import Tooltip from '../../../components/common/tooltip';
import ViewIcon from '../../../assets/icons/view.svg?react';
import { Dispatch } from 'react';
import {
  getCollapsedQuestionTextLines,
  getCollapsedSegments,
  getCollapsedSegmentsContinuation,
} from '../../../utils/common-utils.ts';

type QuizQuestionItemProps = {
  quizQuestionData: ArrayElement<QuizFragmentFragment['quizQuestions']>;
  index: number;
  isExpanded: boolean;
  onInsertQuestion?: () => void;
  onChangeQuestion?: () => void;
  onRemoveQuestion?: () => void;
  onClickQuestion: () => void;
  setPreviewQuestionId: Dispatch<React.SetStateAction<number | undefined>>;
};

const QuizQuestionItem = ({
  quizQuestionData,
  index,
  isExpanded,
  onInsertQuestion,
  onChangeQuestion,
  onRemoveQuestion,
  onClickQuestion,
  setPreviewQuestionId,
}: QuizQuestionItemProps): JSX.Element => {
  const yDragControls = useDragControls();
  // String-based extraction for the conditional logic
  const collapsedContentLines = getCollapsedQuestionTextLines(
    quizQuestionData.question?.content,
    [88],
  );
  const hasCollapsedContent =
    Boolean(collapsedContentLines[0]?.trim()) &&
    !/^no info\b/i.test(collapsedContentLines[0].trim());

  // Formatted segments for rendering
  const contentSegments = getCollapsedSegments(quizQuestionData.question?.content, 88);
  const promptSegments = getCollapsedSegments(quizQuestionData.question?.prompt, 88);
  const nameSegments = hasCollapsedContent ? contentSegments : (promptSegments.length ? promptSegments : []);
  // When there's no content, row 2 continues the prompt from where row 1 left off
  const promptDisplaySegments = hasCollapsedContent
    ? promptSegments
    : getCollapsedSegmentsContinuation(quizQuestionData.question?.prompt, 88, 72);

  return (
    <Reorder.Item
      layout="position"
      dragControls={yDragControls}
      dragListener={false}
      value={quizQuestionData}
      className={'my-0.5 flex select-none items-center'}
    >
      {quizQuestionData.question && (
        <QuizQuestion
          className={'w-full'}
          isCollapsed={!isExpanded}
          key={quizQuestionData.id}
          questionName={
            isExpanded
              ? createExpandedQuestionNameForQuiz(
                  quizQuestionData.question.originalId,
                  quizQuestionData.question.content,
                  quizQuestionData.question.prompt,
                )
              : (
                  <span className={'flex min-w-0 items-center gap-1.5 leading-tight'}>
                    <span
                      className={
                        'shrink-0 text-xs font-normal uppercase tracking-[0.02em] text-gray'
                      }
                    >
                      {`QID: ${quizQuestionData.question.originalId}`}
                    </span>
                    <span
                      className={
                        'min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap'
                      }
                    >
                      {nameSegments.length ? (
                        <InlineFormattedText segments={nameSegments} />
                      ) : (
                        'No preview'
                      )}
                    </span>
                  </span>
                )
          }
          collapsedPrompt={
            promptDisplaySegments.length ? (
              <InlineFormattedText segments={promptDisplaySegments} />
            ) : null
          }
          number={index + 1}
          onInsertQuestion={onInsertQuestion}
          onChangeQuestion={onChangeQuestion}
          onRemoveQuestion={onRemoveQuestion}
          onClickQuestion={onClickQuestion}
          actionsRow={
            <div className={'flex shrink-0 items-center gap-0.5'}>
              <div
                className={'cursor-grab p-0.5 transition-all hover:scale-125'}
                onPointerDown={(event) => yDragControls.start(event)}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <DragAndPropIcon />
              </div>

              <Tooltip tip={'Preview'}>
                <span
                  className={'cursor-pointer p-0.5'}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewQuestionId(() => quizQuestionData!.question!.id);
                  }}
                >
                  <ViewIcon />
                </span>
              </Tooltip>
            </div>
          }
        >
          {isExpanded && (
            <div className={'flex shrink-0 items-center gap-0.5'}>
              <div
                className={'cursor-grab p-0.5 transition-all hover:scale-125'}
                onPointerDown={(event) => yDragControls.start(event)}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <DragAndPropIcon />
              </div>

              <Tooltip tip={'Preview'}>
                <span
                  className={'cursor-pointer p-0.5'}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewQuestionId(() => quizQuestionData!.question!.id);
                  }}
                >
                  <ViewIcon />
                </span>
              </Tooltip>
            </div>
          )}
        </QuizQuestion>
      )}
    </Reorder.Item>
  );
};

export default QuizQuestionItem;
